import * as React from 'react';
import { Group, Text, Stepper, StepperProps, useMantineTheme, createStyles, rem } from '@mantine/core';
import { IconCheck, IconAlertCircleFilled } from '@tabler/icons-react';
import { useTranslation, Trans } from 'react-i18next';
import { openConfirmModal } from '@mantine/modals';

// Components
import { FormStepperWrapper, FormStepperWrapperHandle } from './FormStepperWrapper';
import { PrimaryText } from '../../PrimaryText';

// Helpers
import { consolidateSteps } from '../helpers/sectionHelpers';

// Hooks
import { useStaticKey } from '../hooks/useStaticKey';

//Types
import { IStep, IStepExtended } from '../types';

const useStyles = createStyles((theme, { furthestStep, iconSize }: { furthestStep: number; iconSize: number }) => ({
    stepBody: {
        marginLeft: 0,
        [theme.fn.smallerThan('sm')]: {
            width: '100%',
            paddingRight: '0.75rem'
        }
    },
    stepLabel: {
        fontSize: '0.8rem',
        minWidth: '8rem'
    },
    step: {
        padding: 0,
        minHeight: 0,
        height: '2rem',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 0,
        position: 'relative',
        // paddingRight: '0.5rem',
        paddingRight: '2.25rem',
        paddingLeft: '1.25rem',
        width: '100%',
        pointerEvents: 'auto',
        [`&:not([data-progress]):not([data-completed]):nth-of-type(n+${furthestStep + 2})`]: {
            cursor: 'not-allowed',
            '.mantine-Stepper-stepIcon': {
                display: 'none',
                borderColor: 'transparent',

                background: 'transparent',
                color: theme.colors.gray[4]
            },
            '.mantine-Stepper-stepLabel': {
                color: theme.colors.gray[4]
            },
            '.mantine-Stepper-verticalSeparator': {
                display: 'none'
            }
        },
        '&[data-progress]': {
            background: theme.fn.darken(
                theme.fn.variant({ variant: 'light', color: theme.primaryColor }).background ?? '#fff',
                0.02
            ),
            color: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).color,
            // background: theme.colors.blue[0],
            // color: theme.colors.blue[6],
            '.mantine-Stepper-stepLabel': {
                color: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).color
            },
            '.mantine-Stepper-stepIcon': {
                display: 'none'
            },
            '&[data-highlight-step="true"]': {
                background: theme.colors.orange[1],
                color: theme.colors.orange[7],
                '.mantine-Stepper-stepLabel': {
                    color: theme.colors.orange[7]
                }
            }
        }
    },

    verticalSeparator: {
        display: 'none',
        left: `calc(${iconSize}rem / 2)`
    },

    stepIcon: {
        borderWidth: rem(2),
        color: 'transparent',
        fontSize: '0.8rem',
        width: `${iconSize}rem`,
        minWidth: `${iconSize}rem`,
        height: 0,
        background: 'transparent',
        borderColor: 'transparent',
        '&[data-progress]': {
            height: `${iconSize}rem`
        },
        '&[data-completed]': {
            height: `${iconSize}rem`,
            background: 'transparent',
            borderColor: 'transparent'
        }
    },

    stepWrapper: {
        position: 'absolute',
        right: '0.5rem'
    },

    stepIconVisible: {
        height: `${iconSize}rem`
    },

    separator: {
        marginLeft: rem(-2),
        marginRight: rem(-2),
        height: rem(8),
        background: theme.colors.gray[2]
    },

    stepCompletedIcon: {
        // transform: 'scale(0.6) !important'
    },
    tooltipAnchor: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
    },
    nodeTooltipLabel: {
        fontWeight: 400
    },
    stepStyle: {
        position: 'relative'
    },
    stepInactive: {
        cursor: 'not-allowed'
    },
    stepCount: {
        fontSize: '0.6rem',
        position: 'absolute',
        right: '8px'
    },
    highlightIcon: {
        position: 'absolute',
        right: '5px',
        '&:after': {
            content: '""',
            borderRadius: '1rem',
            width: '1rem',
            height: '1rem',
            position: 'absolute',
            top: '50%',
            right: '50%',
            marginRight: '-0.5rem',
            marginTop: '-0.5rem',
            background: '#f8f9fa'
        }
    },
    highlight: {
        color: theme.colors.orange[6]
    }
}));

export interface FormStepperProps extends Omit<StepperProps, 'children'> {
    steps?: IStep[];
    allowNavigationForwardToStep: number;
    showReviewStep?: boolean;
    highlightedQuestions?: string[];
    isSectionDirty?: boolean;
    discardChanges: () => void;
}

export const FormStepper: React.FC<FormStepperProps> = ({
    allowNavigationForwardToStep,
    onStepClick,
    steps = [],
    active,
    showReviewStep,
    highlightedQuestions,
    isSectionDirty,
    discardChanges,
    ...props
}) => {
    const WrapperRef = React.useRef<FormStepperWrapperHandle>(null);
    const { getStaticKeyByName } = useStaticKey();
    const theme = useMantineTheme();
    const { t } = useTranslation();
    const openSaveDirtyModal = React.useCallback(
        ({ discardCurrentStepChanges }: { discardCurrentStepChanges: () => void }) => {
            openConfirmModal({
                modalId: 'saveDirty',
                title: t('modals.saveDirty.title', { ns: 'components' }),
                children: (
                    <Trans
                        t={t}
                        i18nKey={'modals.saveDirty.body'}
                        ns="components"
                        components={{
                            highlight: <PrimaryText />
                        }}
                    ></Trans>
                ),
                cancelProps: { color: 'red', variant: 'outline' },
                labels: {
                    confirm: t('ok', { ns: 'common' }),
                    cancel: t('discardUnsavedChanges', { ns: 'common' })
                },
                onCancel: () => {
                    discardCurrentStepChanges();
                },
                closeOnConfirm: true
            });
        },
        []
    );
    const consolidatedSteps = React.useMemo(() => {
        const revisedSteps = consolidateSteps(steps);

        if (!showReviewStep) return revisedSteps;
        const furthestPossibleStep = revisedSteps.reduce((acc, { substeps }) => {
            return Math.max(acc, Math.max(...substeps));
        }, 0);

        return [
            ...consolidateSteps(steps),
            {
                label: t('review', { ns: 'common' }),
                questions: [],
                hideSubstepCounter: true,
                substeps: [furthestPossibleStep + 1]
            }
        ];
    }, [steps, showReviewStep]);
    const adjustedActive = React.useMemo(() => {
        return consolidatedSteps.findIndex(({ substeps }) => substeps.includes(active));
    }, [consolidatedSteps, active]);
    const adjustedFurthestStep = React.useMemo(() => {
        return consolidatedSteps.findIndex(({ substeps }) => substeps.includes(allowNavigationForwardToStep));
    }, [consolidatedSteps, active]);

    const stepClick = (stepIndex: number) => {
        if (stepIndex > adjustedFurthestStep) return;
        if (isSectionDirty) {
            openSaveDirtyModal({
                discardCurrentStepChanges: () => {
                    discardChanges();
                    onStepClick?.(consolidatedSteps[stepIndex].substeps[0]);
                    WrapperRef?.current?.closeModal?.();
                }
            });
            return;
        }
        onStepClick?.(consolidatedSteps[stepIndex].substeps[0]);
        WrapperRef?.current?.closeModal?.();
    };
    const { classes, cx } = useStyles({ furthestStep: adjustedFurthestStep, iconSize: 1 });
    const sectionsWithWarnings = React.useMemo(() => {
        if (!highlightedQuestions) return [];
        const highlightStaticKeys = highlightedQuestions.map(getStaticKeyByName);

        return consolidatedSteps.reduce<number[]>(
            (acc, { questions = [] }, index) =>
                questions.some((id) => highlightStaticKeys.includes(id)) ? [...acc, index] : acc,
            []
        );
    }, [getStaticKeyByName, consolidatedSteps, highlightedQuestions]);

    return (
        <FormStepperWrapper
            ref={WrapperRef}
            currentStep={active}
            totalSteps={steps.length}
            label={
                consolidatedSteps?.[adjustedActive] ? (
                    <>
                        <Group position="apart">
                            <Text weight={500}>{consolidatedSteps?.[adjustedActive]?.label}</Text>
                            {consolidatedSteps?.[adjustedActive]?.substeps.includes(active) ? (
                                <Text className={classes.stepCount}>
                                    {consolidatedSteps?.[adjustedActive]?.substeps.indexOf(active) + 1} /{' '}
                                    {consolidatedSteps?.[adjustedActive]?.substeps.length}
                                </Text>
                            ) : null}
                        </Group>
                    </>
                ) : null
            }
        >
            <Stepper
                iconSize={24}
                onStepClick={stepClick}
                breakpoint="sm"
                classNames={{
                    stepBody: classes.stepBody,
                    step: classes.step,
                    stepIcon: classes.stepIcon,
                    stepWrapper: classes.stepWrapper,
                    verticalSeparator: classes.verticalSeparator,
                    stepLabel: classes.stepLabel,
                    separator: classes.separator,
                    stepCompletedIcon: classes.stepCompletedIcon
                }}
                active={adjustedActive}
                {...props}
            >
                {consolidatedSteps.map(({ label, substeps, hideSubstepCounter, reviewStepOnly }, index) => {
                    const highlightSection = sectionsWithWarnings.includes(index);

                    return (
                        <Stepper.Step
                            key={label}
                            style={reviewStepOnly ? { display: 'none' } : {}}
                            label={
                                <Group position="apart">
                                    <Text>{label}</Text>
                                    {highlightSection && !substeps.includes(active) ? (
                                        <div className={classes.highlightIcon}>
                                            <IconAlertCircleFilled
                                                size={18}
                                                stroke={4}
                                                style={{
                                                    color: theme.colors.orange[7],
                                                    zIndex: 1,
                                                    position: 'relative'
                                                }}
                                            />
                                        </div>
                                    ) : null}
                                    {!hideSubstepCounter && substeps.includes(active) ? (
                                        <Text
                                            className={cx(classes.stepCount, {
                                                [classes.highlight]: highlightSection
                                            })}
                                        >
                                            {substeps.indexOf(active) + 1} / {substeps.length}
                                        </Text>
                                    ) : null}
                                </Group>
                            }
                            className={cx(classes.stepStyle)}
                            completedIcon={
                                <IconCheck
                                    size={11}
                                    stroke={4}
                                    style={{
                                        stroke: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).color
                                    }}
                                />
                            }
                            data-highlight-step={highlightSection.toString()}
                        ></Stepper.Step>
                    );
                })}
            </Stepper>
        </FormStepperWrapper>
    );
};
